import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { DsButton } from '@frontend/ui/button';
import { DsSegmentedControlModule } from '@frontend/ui/segmented-control';
import { DsTooltipModule } from '@frontend/ui/tooltip';
import { DeviceService, NavigationService, TrackingService, UserEvent, UserLogoutEvent, UserService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Subject, Subscription, filter } from 'rxjs';

import { RewardsHubConfiguration } from '../common-service/rewards-hub-config';
import { UserBannerService } from '../user-banner/user-banner.service';
import { RewardsHubSegments } from './model';
import { ToggleNavigationService } from './toggle-navigation.service';

const OFFERS_SEGMENT = 'offers';
const REWARDS_SEGMENT = 'rewards';
const REWARDS_ROUTE = '/promo/rewards';

@Component({
    selector: 'toggle-navigation',
    imports: [RouterModule, NgClass, DsSegmentedControlModule, CommonModule, DsTooltipModule, DsButton, IconCustomComponent],
    templateUrl: './toggle-navigation.component.html',
    styleUrls: ['./toggle-navigation.component.scss'],
})
export class ToggleNavigationComponent implements OnInit, OnDestroy {
    isVipUser: boolean;
    private readonly destroy$ = new Subject<void>();
    isTooltipClosed: boolean = false;
    @ViewChild('tooltipContainer') tooltipContainer: ElementRef;
    private contentLoadedSub?: Subscription;
    rewardsHubSegments!: RewardsHubSegments;
    isDataLoaded: boolean = false;

    constructor(
        private router: Router,
        private navigationService: NavigationService,
        public deviceService: DeviceService,
        public userService: UserService,
        public toggleNavigationService: ToggleNavigationService,
        private trackingService: TrackingService,
        private changeDetectorRef: ChangeDetectorRef,
        public rewardsHubConfiguration: RewardsHubConfiguration,
        private userBannerService: UserBannerService,
    ) {}

    ngOnInit() {
        if (this.router.url.includes(REWARDS_SEGMENT)) this.closeInfoTooltip();
        this.userBannerService.isVipUser.subscribe((isVip) => {
            this.isVipUser = isVip;
            document.documentElement.style.setProperty('--toggle-nav-bg-color', isVip ? 'transparent' : 'black');
        });
        if (this.router.url.includes(REWARDS_ROUTE)) this.closeInfoTooltip();

        if (!this.toggleNavigationService.rewardsHubSegments) {
            this.toggleNavigationService.loadDashboardContent();
            this.contentLoadedSub = this.toggleNavigationService.isContentLoaded.subscribe({
                next: () => {
                    this.rewardsHubSegments = this.toggleNavigationService.rewardsHubSegments;
                    this.loadNavigation();
                },
            });
        } else {
            this.rewardsHubSegments = this.toggleNavigationService.rewardsHubSegments;
            this.loadNavigation();
        }

        this.isTooltipClosed = this.toggleNavigationService.isTooltipClosed;

        this.userService.events.pipe(filter((e: UserEvent) => e instanceof UserLogoutEvent)).subscribe(() => this.closeInfoTooltip());
    }

    private loadNavigation(): void {
        this.toggleNavigationService.isPromoActive = !this.router.url.includes(REWARDS_ROUTE);
        this.isDataLoaded = true;
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.contentLoadedSub) {
            this.contentLoadedSub.unsubscribe();
        }
    }

    onToggleNavigation(selectedSegment: string): void {
        const isOffersSelected = selectedSegment === OFFERS_SEGMENT;
        if (selectedSegment.toLowerCase() === REWARDS_SEGMENT) this.closeInfoTooltip();
        if (this.toggleNavigationService.isPromoActive !== isOffersSelected) {
            const targetRoute = isOffersSelected ? this.toggleNavigationService.promoOffersRoute : this.toggleNavigationService.rewardsHubRoute;
            this.navigationService.goTo(targetRoute);
            this.trackNavigationEvent(targetRoute, isOffersSelected);
        }
    }

    private trackNavigationEvent(targetRoute: string, isOffersSelected: boolean): void {
        this.trackingService.triggerEvent('Event.Tracking', {
            'event': 'Event.Tracking',
            'component.CategoryEvent': 'promo hub',
            'component.ActionEvent': 'click',
            'component.LabelEvent': 'offer section',
            'component.PositionEvent': 'not applicable',
            'component.LocationEvent': 'promo hub',
            'component.EventDetails': isOffersSelected ? 'promotions' : 'my rewards',
            'component.URLClicked': window.location.origin + targetRoute || 'not applicable',
            'component.ContentPosition': 'not applicable',
            'campaignId': 'not applicable',
            'page.siteSection': 'promo hub',
            'page.siteSubSection': 'promo offers home page',
            'component.PromotionType': 'not applicable',
            'component.Product': 'not applicable',
            'component.PromoOfferName': 'not applicable',
            'component.PromoIntent': 'not applicable',
        });
    }

    closeInfoTooltip() {
        this.toggleNavigationService.isTooltipClosed = true;
        this.isTooltipClosed = this.toggleNavigationService.isTooltipClosed;
        this.changeDetectorRef.markForCheck();
    }
}
